import classNames from "classnames";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { pause, useFetchThumbFromMediaItem } from "../utils";
import { Loader } from "./Loader";

const MapMarker = ({
  itemId,
  x = 0,
  y = 0,
  modelName,
  highlight,
}: {
  x?: number;
  y?: number;
  itemId: number;
  modelName: string;
  highlight?: boolean;
}): JSX.Element => {
  const [imageSrc, setImageSrc] = useState<string | undefined>();
  const fetchThumb = useFetchThumbFromMediaItem();
  useEffect(() => {
    fetchThumb(modelName, itemId, setImageSrc);
  }, []);
  return (
    <div
      className="absolute w-1 h-1 top-0 left-0"
      style={{
        transform: `translate(${x}px, ${y}px)`,
      }}
    >
      <div
        className={classNames(
          "absolute bottom-0 right-0 bg-black w-[2px] rounded-full",
          {
            "h-[16px]": !highlight,
            "h-[20px]": highlight,
          },
        )}
      >
        <Link to={`/m/${modelName}/show/${itemId}`}>
          <div
            className={classNames(
              "absolute w-12 h-12 bottom-full -left-6 rounded-full overflow-hidden border-black bg-gray-400",
              "transition hover:scale-110",
              {
                "border-2": !highlight,
                "scale-[120%] border-dashed border-2": highlight,
              },
            )}
          >
            <img
              src={imageSrc || "/assets/default-image.jpg"}
              className="object-cover w-full h-full"
            />
          </div>
        </Link>
      </div>
    </div>
  );
};

// Map Node items will always expect X Y fields
// TODO: Migration for X and Y on any map nodes to be floats/numbers
// TODO: Add x/y display for mouse over map

export const MapDisplay = ({
  dataType,
  data,
  clickable = true,
  highlightId,
  mapSrc = "/assets/map.jpg",
  showHint,
}: {
  dataType: string;
  data: any[];
  clickable?: boolean;
  highlightId?: number | string;
  mapSrc?: string;
  showHint?: boolean;
}): JSX.Element => {
  const navigate = useNavigate();
  const [mapSize, setMapSize] = useState<{ w: number; h: number }>({
    w: 100,
    h: 100,
  });
  const [mapLoaded, setMapLoaded] = useState<boolean>(false);
  const [mapError, setMapError] = useState<boolean>(false);

  useEffect(() => {
    loadImage(0);
  }, [mapSrc]);

  const loadImage = async (tries: number) => {
    try {
      const tempImg = new Image();
      tempImg.onload = () => {
        setMapLoaded(true);
        setMapError(false);
      };
      tempImg.onerror = async () => {
        await pause(3000);
        if (tries < 2) {
          loadImage(tries + 1);
        } else {
          setMapError(true);
          setMapLoaded(false);
        }
      };
      tempImg.src = mapSrc;
    } finally {
    }
  };

  const updateMapWidth = () => {
    const mapElement = document.getElementById("map");
    if (mapElement)
      setMapSize({ w: mapElement?.offsetWidth, h: mapElement?.offsetHeight });
  };

  const handleMapClick = (event: React.MouseEvent<HTMLImageElement>) => {
    event.preventDefault();
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = _.round((100 * (event.clientX - bounds.left)) / mapSize.w, 2);
    const y = _.round((100 * (event.clientY - bounds.top)) / mapSize.h, 2);
    navigate(`/m/${dataType}/create?xpos=${x}&ypos=${y}`);
  };

  // change to number
  const hId = _.isNumber(highlightId)
    ? highlightId
    : _.toNumber(highlightId || "-999");

  return !mapLoaded ? (
    <div className="w-full h-full grid grid-rows gap-2 content-center text-gray-500">
      <Loader />
    </div>
  ) : mapError ? (
    <div className="w-full h-full grid content-center text-gray-500">
      <div className="text-xs text-center px-4">Could not load map image</div>
    </div>
  ) : (
    <div
      className={classNames("mb-8 relative", {
        "pointer-events-none": !clickable,
      })}
    >
      <img
        id="map"
        src={mapSrc}
        className="relative"
        onLoad={() => updateMapWidth()}
        onClick={(e) => handleMapClick(e)}
      />
      {showHint && (
        <div className="absolute text-sm bottom-0 left-0 font-medium p-2 opacity-70">
          Click on the map to create a node at that position
        </div>
      )}
      <div className="absolute w-0 h-0 top-0 left-0 z-10">
        <div className="relative w-0 h-0">
          {data.map((item, index) => {
            const xvalue = item.x || item.xpos || 0;
            const yvalue = item.y || item.ypos || 0;
            const xpos = (xvalue / 100) * mapSize.w;
            const ypos = (yvalue / 100) * mapSize.h;
            const highlight = item.id === hId;
            return (
              <MapMarker
                key={index}
                itemId={item.id}
                x={xpos}
                y={ypos}
                modelName={dataType}
                highlight={highlight}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
